import type { Dayjs } from "dayjs";
import { listProviderInviteTasks } from "../../invites/services/inviteTaskService";
import type { DateFilterMode } from "../../shared/helpers/dateRange";
import { isWithinDateFilter } from "../../shared/helpers/dateRange";
import { mergeBookedRows } from "../transformers/bookedRowTransformers";
import type { BookedTeleconsultoriaRow, ProviderInviteRow } from "../types";
import { listAcceptedInviteRows } from "./acceptedInvitesService";
import { listBookedTeleconsultorias } from "./bookedTeleconsultoriaService";
import { toProviderInviteRows } from "./providerInviteRowsService";

export interface ClinicActivity {
  inviteRows: ProviderInviteRow[];
  bookedRows: BookedTeleconsultoriaRow[];
}

/** Data da consulta no convite: prazo (= fim do atendimento) ou, sem ele, o pedido. */
function inviteConsultationDate(task: fhir5.Task): string | undefined {
  return task.restriction?.period?.end || task.authoredOn;
}

/**
 * Visão CLÍNICA da prestadora, no período do filtro:
 * - convites (Tasks) para os profissionais dela;
 * - marcadas = consultas nas agendas + convites ACEITOS no período (ex.: "Agora",
 *   que não fica numa agenda). Cada Appointment aparece uma vez.
 * As Tasks são buscadas uma vez e servem às duas listas.
 */
export async function loadClinicActivity(params: {
  organizationToken: string;
  date: Dayjs;
  mode: DateFilterMode;
  startDate: string;
  endDate: string;
}): Promise<ClinicActivity> {
  const { organizationToken, date, mode, startDate, endDate } = params;
  if (!organizationToken) return { inviteRows: [], bookedRows: [] };

  const [tasks, agendaRows] = await Promise.all([
    listProviderInviteTasks(organizationToken),
    listBookedTeleconsultorias({ organizationToken, startDate, endDate }),
  ]);
  const tasksInRange = tasks.filter((task) =>
    isWithinDateFilter(inviteConsultationDate(task), date, mode),
  );
  const [inviteRows, acceptedRows] = await Promise.all([
    toProviderInviteRows(tasksInRange),
    listAcceptedInviteRows(tasksInRange.filter((task) => task.status === "accepted")),
  ]);
  const acceptedRowsInRange = acceptedRows.filter((row) =>
    isWithinDateFilter(row.start, date, mode),
  );
  return { inviteRows, bookedRows: mergeBookedRows(agendaRows, acceptedRowsInRange) };
}
